<template>
  <div class="deal-list row">
    <div
      id="packages_list_table_container"
      class="col-12">
      <Table
        v-if="isReady"
        id="packages_list_table"
        ref="table"
        category="Packages"
        :allow-selection="selectionEnabled"
        :allow-selection-all="isSupplyOrganization"
        :lock-selection="lockSelection"
        :can-select-item="isDemandOrganization ? isItemSelectable : null"
        :reset-selection-on-update="false"
        :items="packages"
        :columns="columns"
        :table-download-columns="tableDownloadColumns"
        :expand="[
          'package_properties',
          'package_external_exchange_restrictions',
          ...(isCuratorOrganization
            ? [
                'inventory_targeting.co2e_emissions.score',
                'package_children.inventory_targeting.co2e_emissions.score',
                'package_fee',
              ]
            : []),
        ]"
        :show-filters="true"
        :fixed-filters="fixedFilters"
        :total-count="totalCount"
        :text-search="tableTextSearch"
        :sort-column="sortBy"
        :sort-ascending="isSortAscending"
        :is-loading="isLoading"
        :text-search-properties="['package_id', 'deal_id', 'name', 'package_properties.value']"
        :is-main-table="false"
        :is-editable="true"
        :enable-download="true"
        pagination
        @download="(x) => $emit('onDownload', x)"
        @filters-changed="(x) => tableFiltersChangedHandler(x)"
        @selection-changed="(x) => $emit('selection-changed', x)">
        <template #dsp="{ item: deal }">
          <BuyerDetails
            :is-package-tile="true"
            :buyer-organization-d-s-ps="buyerOrganizationDSPs"
            :ids="deal.buyerOrganizationDSPIDs"
            :buyer-external-exchanges="organizationExternalExchanges"
            :buyer-external-exchange-i-ds="deal.buyerExternalExchangeIDs" />
        </template>
        <template #link="{ item: deal }">
          <button
            v-if="isDemandOrganization"
            v-tooltip="
              capitalize(
                $t(
                  activeDeal?.dealID === deal?.dealID
                    ? 'messages.closeDealDetailsPane'
                    : 'messages.showTargetingDetails',
                ),
              )
            "
            data-testid="request-deal-details-button"
            class="btn btn-no-text btn-xxs btn-info-hollow mr-3"
            @click="emits('requestedDealDetails', deal)">
            <component
              :is="activeDeal?.dealID === deal?.dealID ? IconClose : IconEye"
              class="icon" />
          </button>
          <router-link
            v-if="!(isSalesPlanner || isPlannerReserveCampaignIDRole)"
            :to="deal.endpoint"
            class="entity-link"
            style="margin-right: 0.6rem"
            :title="deal.name">
            {{ deal.name }}
          </router-link>
          <span v-else>{{ deal.name }}</span>
          <span class="icon">
            <icon-colored-private-deal
              v-if="deal.isPrivate"
              class="icon" />
          </span>
        </template>
        <template #totalImpressionsFormatted="{ item: deal }">
          <TableColumnLoader v-if="isLoadingPackageDetails" />
          <span v-else>{{ deal.totalImpressionsFormatted }}</span>
        </template>
        <template #averageCPMFormatted="{ item: deal }">
          <TableColumnLoader v-if="isLoadingPackageDetails" />
          <span v-else>{{ deal.averageCPMFormatted }}</span>
        </template>
        <template #totalSpendFormatted="{ item: deal }">
          <TableColumnLoader v-if="isLoadingPackageDetails" />
          <span v-else>{{ deal.totalSpendFormatted }}</span>
        </template>
        <template #mediaOwner="{ item: deal }">
          <span v-if="getMediaOwner(deal.organizationID) !== undefined">
            {{ getMediaOwner(deal.organizationID).name }}
          </span>
        </template>
        <template #status="{ item: deal }">
          <package-status-icon :status="deal.state" />
          {{ capitalize(deal.status) }}
        </template>
        <template #co2EmissionsScore="{ item: deal }">
          <template v-if="deal.co2EmissionsScore > 0">
            <green-score
              style="width: 4.8rem; height: 2.4rem; font-size: 1.4rem"
              :is-animating="false"
              :show-green-leaf="false"
              :inline="true"
              :use-animations="false"
              :co2-score="deal.co2EmissionsScore" />
            <green-score
              v-if="deal.co2Score > 50"
              style="margin-left: 0.6rem; width: 2.4rem; height: 2.4rem; font-size: 1.4rem"
              :use-animations="false"
              :is-animating="false"
              :co2-score="deal.co2EmissionsScore" />
          </template>
          <span v-else>{{ DASH }}</span>
        </template>
        <template #packageProperties="{ item: deal }">
          <template
            v-if="
              organizationCustomFields != null &&
              deal.packageProperties.filter((x) => organizationCustomFields.find((y) => y.key === x.key) != null)
                .length > 0
            ">
            <span
              v-for="prop in deal.packageProperties.filter(
                (x) => organizationCustomFields.find((y) => y.key === x.key) != null,
              )"
              :key="prop.key"
              class="badge badge-info-hollow mr-3">
              <b
                >{{
                  organizationCustomFields.find((x) => x.key === prop.key) != null
                    ? organizationCustomFields.find((x) => x.key === prop.key).name
                    : prop.key
                }}:</b
              >
              {{ prop.value }}
            </span>
          </template>
          <span v-else>{{ DASH }}</span>
        </template>
        <template #bidRequestSentInTheLastHour="{ item: deal }">
          <TableColumnLoader v-if="isLoadingBiddingMetrics" />
          <div v-else>
            <span
              v-if="deal.isBiddingMetricsVisible"
              class="badge"
              :class="{
                'badge-success': deal.bidRequestSentInTheLastHour,
                'badge-danger': !deal.bidRequestSentInTheLastHour,
              }">
              {{ deal.bidRequestSentInTheLastHourDescription }}
            </span>
            <span v-else>{{ DASH }}</span>
          </div>
        </template>
        <template #bidResponseInTheLastHour="{ item: deal }">
          <TableColumnLoader v-if="isLoadingBiddingMetrics" />
          <div v-else>
            <span
              v-if="deal.isBiddingMetricsVisible"
              class="badge"
              :class="{
                'badge-success': deal.bidResponseInTheLastHour,
                'badge-danger': !deal.bidResponseInTheLastHour,
              }">
              {{ deal.bidResponseInTheLastHourDescription }}
            </span>
            <span v-else>{{ DASH }}</span>
          </div>
        </template>
        <template #didPlayedInTheLastHour="{ item: deal }">
          <TableColumnLoader v-if="isLoadingBiddingMetrics" />
          <div v-else>
            <span
              v-if="!deal.isForBidding"
              class="badge"
              :class="{
                'badge-success': deal.didPlayedInTheLastHour,
                'badge-danger': !deal.didPlayedInTheLastHour,
              }">
              {{ deal.didPlayedInTheLastHourDescription }}
            </span>
            <span v-else>{{ DASH }}</span>
          </div>
        </template>
        <template #createdBy="{ item: deal }">
          <UserDetails :user-i-d="deal.apiObject.created_by" />
        </template>

        <template #planID="{ item: deal }">{{ deal.planIDFormatted }}</template>

        <template #no-records>
          <no-records-table
            :icon="IconComplexDeal2"
            :header-text="
              isSupplyOrganization || isExchange
                ? i18n.t('messages.tablePlaceholderNoDealsTitleSupplySide')
                : i18n.t('messages.tablePlaceholderNoCuratedDeals')
            "
            :details-text="
              isSupplyOrganization || isExchange
                ? `${i18n.t('messages.tablePlaceholderNoDealsDescriptionSupplySide')}!`
                : i18n.t('messages.tablePlaceholderNoCuratedDealsDescription')
            "
            :action-text="
              isSupplyOrganization || isExchange
                ? capitalize(i18n.t('actions.createADeal'))
                : capitalize(i18n.t('actions.createCuratedDeal'))
            "
            :action-handler="
              isSupplyOrganization || isExchange
                ? () => $router.push(newDealRoute)
                : () => $router.push('/curate/deals/new/edit')
            " />
        </template>
        <template #no-match>
          <no-records-table
            :icon="markRaw(IconComplexDeal2)"
            :header-text="i18n.t('messages.tablePlaceholderNoMatchingDealsTitle')"
            :details-text="i18n.t('messages.tablePlaceholderNoMatchingItemsDescription')" />
        </template>
      </Table>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, markRaw, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnLoader from '@/common/components/controls/table/TableColumnLoader.vue';
  import BuyerDetails from '@/common/components/deals/BuyerDetails.vue';
  import { isItemSelectable } from '@/common/components/deals/DealsHelper';
  import PackageStatusIcon from '@/common/components/deals/PackageStatusIcon.vue';
  import { IconColoredPrivateDeal } from '@/common/icons/colored';
  import { IconComplexDeal2 } from '@/common/icons/complex';
  import { IconClose, IconEye } from '@/common/icons/monochrome';
  import { OrganizationCustomField, OrganizationDSP, Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import ExternalExchange from '@/common/models/misc-api-models/ExternalExchange';
  import Filter from '@/common/models/query-params/Filter';
  import { DASH } from '@/globals';
  import { ISortBy } from '@/org-app/components/packages/DealSorting';
  import UserDetails from '@/org-app/components/users/UserDetails.vue';
  import { mapGetters } from '@/utils/StateMapping';

  interface IDealsTableProps {
    packages: Package[];
    mediaOwners: MediaOwner[];
    buyerOrganizationDSPs: OrganizationDSP[];
    organizationExternalExchanges: ExternalExchange[];
    organizationCustomFields?: OrganizationCustomField[];
    fixedFilters: Filter[];
    columns: Record<string, TableColumn>;
    tableDownloadColumns: Record<string, TableColumn>;
    sortBy?: ISortBy;
    totalCount: number;
    pageSize: number;
    tableTextSearch?: string;
    newDealRoute: string;
    isReady: boolean;
    isLoading: boolean;
    isSalesPlanner?: boolean;
    isPlannerReserveCampaignIDRole?: boolean;
    isExchange?: boolean;
    isSortAscending: boolean;
    isLoadingPackageDetails: boolean;
    isLoadingBiddingMetrics?: boolean;
    previousTableData: typeof Table | null;
    selectionEnabled?: boolean;
    lockSelection?: boolean;
    activeDeal?: Package;
  }

  const props = withDefaults(defineProps<IDealsTableProps>(), {
    packages: () => [],
    mediaOwners: () => [],
    buyerOrganizationDSPs: () => [],
    organizationExternalExchanges: () => [],
    organizationCustomFields: null,
    fixedFilters: null,
    columns: null,
    tableDownloadColumns: null,
    sortBy: null,
    totalCount: 0,
    pageSize: 10,
    tableTextSearch: '',
    isReady: false,
    isLoading: true,
    isSalesPlanner: false,
    isPlannerReserveCampaignIDRole: false,
    isExchange: false,
    isLoadingPackageDetails: true,
    isLoadingBiddingMetrics: true,
    previousTableData: null,
    selectionEnabled: true,
    lockSelection: false,
    activeDeal: null,
  });

  const emits = defineEmits([
    'onTableLoaded',
    'onDownload',
    'onTableFiltersChanged',
    'selection-changed',
    'requestedDealDetails',
  ]);

  const {
    'orgApp/isSupplyOrganization': isSupplyOrganization,
    'orgApp/isCuratorOrganization': isCuratorOrganization,
    'orgApp/isDemandOrganization': isDemandOrganization,
  } = mapGetters();

  const i18n = useI18n();

  const table = ref(null);
  const restoredData = ref(props.previousTableData);

  watch(
    () => table.value,
    () => {
      if (table.value) emits('onTableLoaded', table.value);
    },
  );

  const getMediaOwner = (id) => {
    return props.mediaOwners.find((x) => x.id === id);
  };

  const tableFiltersChangedHandler = (x) => {
    if (restoredData.value) {
      restoredData.value = null;
      return;
    }
    emits('onTableFiltersChanged', x);
  };
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
</style>
